import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { Player, Team, saveTeam, getMatch } from '../services/matchService';
import { db } from '../config/firebase';

const OpponentTeamConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('matchId');
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentPlayer, setCurrentPlayer] = useState<Partial<Player>>({
    number: 1
  });
  const [numberInput, setNumberInput] = useState<string>('1');

  // Pas de limite prédéfinie pour les numéros
  // Renvoie le plus petit numéro libre >= 1 à partir d'une liste
  const getNextFreeNumber = (numbers: number[]) => {
    const taken = new Set(numbers.filter(n => Number.isInteger(n) && n > 0));
    let n = 1;
    while (taken.has(n)) n++;
    return n;
  };

  // Ajoute 14 joueurs anonymes: Prénom = 'J', Nom = '1'..'14'
  const handleAddAnonymousBatch = () => {
    const used = new Set(players.map(p => p.number));
    const newPlayers: Player[] = [];
    for (let i = 1; i <= 14; i++) {
      // trouver le plus petit numéro libre en tenant compte des ajouts en cours
      let candidate = 1;
      while (used.has(candidate)) candidate++;
      used.add(candidate);
      newPlayers.push({ firstName: 'J', lastName: String(i), number: candidate });
    }
    setPlayers(prev => [...prev, ...newPlayers]);
    // Met à jour le formulaire avec le prochain numéro libre après insertion
    const next = getNextFreeNumber(Array.from(used));
    setCurrentPlayer({ firstName: '', lastName: '', number: next });
    setNumberInput(String(next));
  };

  // Charger les données de l'équipe adverse si on a un matchId
  useEffect(() => {
    const loadTeamData = async () => {
      if (matchId) {
        try {
          console.log('Chargement des données pour le match:', matchId);
          const match = await getMatch(matchId);
          
          if (match && match.teamB) {
            console.log('Données de l\'\u00e9quipe B trouvées:', match.teamB);
            // Charger le nom de l'équipe
            setTeamName(match.teamB.name || '');
            
            // Charger les joueurs
            if (match.teamB.players && match.teamB.players.length > 0) {
              setPlayers(match.teamB.players);
              
              // Déterminer le prochain numéro disponible pour le nouveau joueur
              const maxNumber = Math.max(...match.teamB.players.map(p => p.number), 0);
              const nextNumber = maxNumber + 1;
              setCurrentPlayer(prev => ({ ...prev, number: nextNumber }));
              setNumberInput(nextNumber.toString());
            }
          } else {
            console.log('Aucune donnée d\'\u00e9quipe adverse trouvée, utilisation des valeurs par défaut');
            // Si pas de données d'équipe, utiliser les valeurs du localStorage
            const storedTeamBName = localStorage.getItem('teamBName');
            if (storedTeamBName) {
              setTeamName(storedTeamBName);
            }
          }
        } catch (error) {
          console.error('Erreur lors du chargement des données de l\'\u00e9quipe adverse:', error);
          alert('Impossible de charger les données de l\'\u00e9quipe adverse. Veuillez réessayer.');
        }
      } else {
        // Si pas de matchId, utiliser les valeurs du localStorage
        const storedTeamBName = localStorage.getItem('teamBName');
        if (storedTeamBName) {
          setTeamName(storedTeamBName);
        }
      }
    };
    
    loadTeamData();
  }, [matchId]);


  const handleAddPlayer = () => {
    // Validation
    if (!currentPlayer.firstName || !currentPlayer.lastName || !currentPlayer.number) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    // Vérifier les doublons de numéro
    if (players.some(p => p.number === currentPlayer.number)) {
      alert('Ce numéro est déjà utilisé');
      return;
    }

    setPlayers([...players, currentPlayer as Player]);

    // Suggérer le prochain numéro disponible (incrémenter de 1 par rapport au plus grand numéro utilisé)
    const usedNumbers = [...players, currentPlayer as Player].map(p => p.number);
    const nextNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 1;

    // Réinitialiser le formulaire avec le prochain numéro suggéré
    setCurrentPlayer({
      firstName: '',
      lastName: '',
      number: nextNumber
    });
    setNumberInput(nextNumber.toString());
  };

  const handleRemovePlayer = (index: number) => {
    const newPlayers = players.filter((_, i) => i !== index);
    setPlayers(newPlayers);
  };

  const handleSaveTeam = async () => {
    try {
      // Validation
      if (!teamName) {
        alert('Veuillez saisir le nom de l\'équipe');
        return;
      }

      if (players.length === 0) {
        alert('Veuillez ajouter au moins un joueur');
        return;
      }

      const teamData: Team = {
        name: teamName,
        players: players
      };

      // Sauvegarder l'équipe
      const teamId = await saveTeam(teamData);

      // Si on a un matchId, mettre à jour le match avec l'ID de l'équipe B
      if (matchId) {
        const match = await getMatch(matchId);
        if (match) {
          const matchRef = doc(db, 'matches', matchId);
          await updateDoc(matchRef, {
            'teamB.id': teamId,
            'teamB.players': players,
            'status': 'live' // On met le match en statut live une fois les deux équipes configurées
          });
        }
      }

      // Rediriger vers la page de match live avec le matchId
      if (matchId) {
        navigate(`/live-match/${matchId}`);
      } else {
        navigate('/matches');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'équipe adverse:', error);
      alert('Impossible de sauvegarder l\'équipe. Veuillez réessayer.');
    }
  };

  

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">
        {matchId ? 'Modification de l\'\u00c9quipe Adverse' : 'Configuration de l\'\u00c9quipe Adverse'}
      </h1>

      {/* Nom de l'équipe */}
      <div className="mb-6 relative border-2 border-gray-200 rounded-lg p-6 pt-10">
        <div className="absolute -top-5 -left-2 bg-blue-600 text-white text-2xl font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
          1
        </div>
        <label className="block text-gray-700 text-lg font-bold mb-2">
          Nom de l'équipe
        </label>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="Entrez le nom de l'équipe"
        />
      </div>

      {/* Formulaire d'ajout de joueur */}
      <div className="mb-6 p-6 bg-gray-50 rounded-lg relative border-2 border-gray-200 pt-10">
        <div className="absolute -top-5 -left-2 bg-blue-600 text-white text-2xl font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
          2
        </div>
        <h2 className="text-xl font-semibold mb-4">Ajouter un joueur</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Prénom
            </label>
            <input
              type="text"
              value={currentPlayer.firstName || ''}
              onChange={(e) => setCurrentPlayer({ ...currentPlayer, firstName: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Prénom"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Nom
            </label>
            <input
              type="text"
              value={currentPlayer.lastName || ''}
              onChange={(e) => setCurrentPlayer({ ...currentPlayer, lastName: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="Nom"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Numéro
            </label>
            <input
              type="number"
              min="1"
              value={numberInput}
              onChange={(e) => {
                const value = e.target.value;
                setNumberInput(value);
                if (value && parseInt(value) > 0) {
                  setCurrentPlayer({ ...currentPlayer, number: parseInt(value) });
                }
              }}
              className="w-full p-2 border rounded"
              placeholder="Numéro du joueur"
            />
            {players.some(p => p.number === currentPlayer.number) && (
              <p className="text-red-500 text-xs mt-1">Ce numéro est déjà utilisé</p>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <button
            onClick={handleAddPlayer}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Ajouter le joueur
          </button>
          <button
            onClick={handleAddAnonymousBatch}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            Ajouter 11 joueurs type
          </button>
        </div>
      </div>

      {/* Liste des joueurs */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Joueurs ({players.length})</h2>
        <div className="space-y-2">
          {players.map((player, index) => (
            <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span>
                #{player.number} - {player.firstName} {player.lastName}
              </span>
              <button
                onClick={() => handleRemovePlayer(index)}
                className="text-red-500 hover:text-red-700"
              >
                Supprimer
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Boutons de navigation */}
      <div className="relative border-2 border-gray-200 rounded-lg p-4 pt-10">
        <div className="absolute -top-5 -left-2 bg-blue-600 text-white text-2xl font-bold rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
          3
        </div>
        <div className="flex gap-4">
          <button
            onClick={handleSaveTeam}
            className="flex-1 bg-green-500 text-white py-4 rounded-lg hover:bg-green-600 font-bold text-lg"
          >
            {matchId ? "Mettre à jour l'Équipe" : "Sauvegarder l'Équipe"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpponentTeamConfigPage;
